import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FaChevronLeft, FaChevronRight, FaSearch, FaSort, FaSortDown, FaSortUp } from 'react-icons/fa';
import { cn } from '../../../utils/cn';
import { compareBy, type SortDirection } from '../utils';
import { EmptyState } from './primitives';

export interface Column<T> {
  key: string;
  header: string;
  accessor: (row: T) => React.ReactNode;
  sortValue?: (row: T) => string | number;
  align?: 'left' | 'right' | 'center';
  width?: string;
  cellClassName?: string;
  headerClassName?: string;
  /** The clickable identity column; its content also acts as the row-open control. */
  primary?: boolean;
}

export interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  getRowId: (row: T) => string;
  onRowClick?: (row: T) => void;
  loading?: boolean;

  searchable?: boolean;
  searchPlaceholder?: string;
  searchText?: (row: T) => string;

  selectable?: boolean;
  selectedIds?: string[];
  onSelectionChange?: (ids: string[]) => void;
  bulkActions?: (ids: string[]) => React.ReactNode;

  pageSize?: number;
  pageSizeOptions?: number[];

  toolbar?: React.ReactNode;
  actions?: React.ReactNode;
  initialSort?: { key: string; direction: SortDirection };

  emptyTitle?: string;
  emptyMessage?: string;
  emptyIcon?: React.ReactNode;
  dense?: boolean;
  stickyHeader?: boolean;
  maxBodyHeight?: string;
}

const alignClass = (align?: 'left' | 'right' | 'center') =>
  align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';

export function DataTable<T>(props: DataTableProps<T>) {
  const {
    columns,
    data,
    getRowId,
    onRowClick,
    loading = false,
    searchable = false,
    searchPlaceholder = 'Search…',
    searchText,
    selectable = false,
    selectedIds = [],
    onSelectionChange,
    bulkActions,
    pageSize: initialPageSize = 10,
    pageSizeOptions = [10, 25, 50],
    toolbar,
    actions,
    initialSort,
    emptyTitle = 'Nothing here yet',
    emptyMessage,
    emptyIcon,
    dense = false,
    stickyHeader = true,
    maxBodyHeight,
  } = props;

  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<{ key: string; direction: SortDirection } | null>(initialSort ?? null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const headerCheckboxRef = useRef<HTMLInputElement>(null);

  const filtered = useMemo(() => {
    if (!searchable || !searchText || !query.trim()) return data;
    const q = query.trim().toLowerCase();
    return data.filter((row) => searchText(row).toLowerCase().includes(q));
  }, [data, query, searchable, searchText]);

  const sorted = useMemo(() => {
    if (!sort) return filtered;
    const column = columns.find((c) => c.key === sort.key);
    if (!column?.sortValue) return filtered;
    const getKey = column.sortValue;
    return [...filtered].sort((a, b) => compareBy(a, b, getKey, sort.direction));
  }, [filtered, sort, columns]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageRows = useMemo(
    () => sorted.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [sorted, currentPage, pageSize],
  );

  // Reset to first page whenever the result set shrinks below the current page.
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const filteredIds = useMemo(() => filtered.map(getRowId), [filtered, getRowId]);
  const selectedInView = filteredIds.filter((id) => selectedIds.includes(id));
  const allSelected = filteredIds.length > 0 && selectedInView.length === filteredIds.length;
  const someSelected = selectedInView.length > 0 && !allSelected;

  useEffect(() => {
    if (headerCheckboxRef.current) headerCheckboxRef.current.indeterminate = someSelected;
  }, [someSelected]);

  const toggleSort = (column: Column<T>) => {
    if (!column.sortValue) return;
    setSort((prev) => {
      if (prev?.key !== column.key) return { key: column.key, direction: 'asc' };
      if (prev.direction === 'asc') return { key: column.key, direction: 'desc' };
      return null;
    });
  };

  const toggleAll = () => {
    if (!onSelectionChange) return;
    if (allSelected) onSelectionChange(selectedIds.filter((id) => !filteredIds.includes(id)));
    else onSelectionChange([...new Set([...selectedIds, ...filteredIds])]);
  };

  const toggleOne = (id: string) => {
    if (!onSelectionChange) return;
    onSelectionChange(selectedIds.includes(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id]);
  };

  const colSpan = columns.length + (selectable ? 1 : 0);
  const cellPad = dense ? 'px-3 py-2' : 'px-4 py-3';

  return (
    <div
      className="rounded-xl border"
      style={{ backgroundColor: 'var(--color-background-paper)', borderColor: 'var(--color-border-light)' }}
    >
      {(searchable || toolbar || actions) && (
        <div className="flex flex-col gap-3 border-b p-4 sm:flex-row sm:items-center sm:justify-between" style={{ borderColor: 'var(--color-border-light)' }}>
          <div className="flex flex-1 flex-wrap items-center gap-3">
            {searchable && (
              <div className="relative w-full sm:w-72">
                <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center" style={{ color: 'var(--color-text-secondary)' }}>
                  <FaSearch className="h-3.5 w-3.5" />
                </span>
                <input
                  type="text"
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setPage(1);
                  }}
                  placeholder={searchPlaceholder}
                  aria-label="Search table"
                  className="w-full rounded-lg border py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-1"
                  style={{
                    backgroundColor: 'var(--color-background-default)',
                    borderColor: 'var(--color-border-light)',
                    color: 'var(--color-text-primary)',
                    ['--tw-ring-color' as string]: 'var(--color-primary-500)',
                  }}
                />
              </div>
            )}
            {toolbar}
          </div>
          {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
        </div>
      )}

      {selectable && selectedInView.length > 0 && (
        <div
          className="flex flex-wrap items-center justify-between gap-3 border-b px-4 py-2.5"
          style={{ backgroundColor: 'var(--color-primary-50)', borderColor: 'var(--color-border-light)' }}
        >
          <span className="text-sm font-semibold" style={{ color: 'var(--color-primary-800)' }}>
            {selectedInView.length} selected
          </span>
          <div className="flex items-center gap-2">
            {bulkActions?.(selectedInView)}
            <button
              type="button"
              onClick={() => onSelectionChange?.(selectedIds.filter((id) => !filteredIds.includes(id)))}
              className="text-xs font-semibold underline"
              style={{ color: 'var(--color-primary-700)' }}
            >
              Clear
            </button>
          </div>
        </div>
      )}

      <div className="overflow-x-auto" style={maxBodyHeight ? { maxHeight: maxBodyHeight, overflowY: 'auto' } : undefined}>
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr style={{ backgroundColor: 'var(--color-background-paper)' }}>
              {selectable && (
                <th
                  className={cn(cellPad, 'w-10', stickyHeader && 'sticky top-0 z-10')}
                  style={{ backgroundColor: 'var(--color-background-paper)', borderBottom: '1px solid var(--color-border-light)' }}
                >
                  <input
                    ref={headerCheckboxRef}
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    aria-label="Select all rows"
                    className="h-4 w-4 cursor-pointer accent-[var(--color-primary-500)]"
                  />
                </th>
              )}
              {columns.map((column) => {
                const isSorted = sort?.key === column.key;
                return (
                  <th
                    key={column.key}
                    className={cn(cellPad, 'text-xs font-semibold uppercase tracking-wide', alignClass(column.align), stickyHeader && 'sticky top-0 z-10', column.headerClassName)}
                    style={{ color: 'var(--color-text-secondary)', backgroundColor: 'var(--color-background-paper)', borderBottom: '1px solid var(--color-border-light)', width: column.width }}
                    aria-sort={isSorted ? (sort?.direction === 'asc' ? 'ascending' : 'descending') : undefined}
                  >
                    {column.sortValue ? (
                      <button
                        type="button"
                        onClick={() => toggleSort(column)}
                        className={cn('inline-flex items-center gap-1.5 hover:opacity-80', column.align === 'right' && 'flex-row-reverse')}
                        style={{ color: isSorted ? 'var(--color-text-primary)' : 'var(--color-text-secondary)' }}
                      >
                        {column.header}
                        {isSorted ? (
                          sort?.direction === 'asc' ? <FaSortUp className="h-3 w-3" /> : <FaSortDown className="h-3 w-3" />
                        ) : (
                          <FaSort className="h-3 w-3 opacity-40" />
                        )}
                      </button>
                    ) : (
                      column.header
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={`skeleton-${i}`} style={{ borderBottom: '1px solid var(--color-border-light)' }}>
                  <td colSpan={colSpan} className={cellPad}>
                    <div className="h-4 w-full animate-pulse rounded" style={{ backgroundColor: 'var(--color-hover-background)' }} />
                  </td>
                </tr>
              ))
            ) : pageRows.length === 0 ? (
              <tr>
                <td colSpan={colSpan}>
                  <EmptyState icon={emptyIcon} title={emptyTitle} message={emptyMessage} />
                </td>
              </tr>
            ) : (
              pageRows.map((row) => {
                const id = getRowId(row);
                const isSelected = selectedIds.includes(id);
                return (
                  <tr
                    key={id}
                    className={cn('group relative transition-colors', onRowClick && 'hover:bg-[var(--color-hover-background)]')}
                    style={{ borderBottom: '1px solid var(--color-border-light)', backgroundColor: isSelected ? 'var(--color-primary-50)' : undefined }}
                  >
                    {selectable && (
                      <td className={cn(cellPad, 'relative z-10 w-10')}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleOne(id)}
                          aria-label="Select row"
                          className="h-4 w-4 cursor-pointer accent-[var(--color-primary-500)]"
                        />
                      </td>
                    )}
                    {columns.map((column, index) => (
                      <td
                        key={column.key}
                        className={cn(cellPad, alignClass(column.align), 'align-middle', (column.primary || (index === 0 && onRowClick)) && 'relative z-10', column.cellClassName)}
                        style={{ color: 'var(--color-text-primary)' }}
                      >
                        {column.primary && onRowClick ? (
                          <button
                            type="button"
                            onClick={() => onRowClick(row)}
                            className="text-left font-semibold hover:underline focus:outline-none focus:underline"
                            style={{ color: 'var(--color-primary-600)' }}
                          >
                            {column.accessor(row)}
                          </button>
                        ) : (
                          column.accessor(row)
                        )}
                      </td>
                    ))}
                    {onRowClick && !columns.some((c) => c.primary) && (
                      <td className="relative z-10 pr-2">
                        <button
                          type="button"
                          onClick={() => onRowClick(row)}
                          aria-label="Open row"
                          className="rounded p-1.5 opacity-0 transition-opacity group-hover:opacity-100"
                          style={{ color: 'var(--color-text-secondary)' }}
                        >
                          <FaChevronRight className="h-3 w-3" />
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {!loading && sorted.length > 0 && (
        <div className="flex flex-col gap-3 border-t px-4 py-3 text-sm sm:flex-row sm:items-center sm:justify-between" style={{ borderColor: 'var(--color-border-light)' }}>
          <div className="flex items-center gap-2" style={{ color: 'var(--color-text-secondary)' }}>
            <span>
              {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, sorted.length)} of {sorted.length}
            </span>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
              aria-label="Rows per page"
              className="rounded-md border px-2 py-1 text-xs"
              style={{ backgroundColor: 'var(--color-background-default)', borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)' }}
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>
                  {size} / page
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={currentPage <= 1}
              aria-label="Previous page"
              className="rounded-md border p-2 disabled:opacity-40"
              style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)' }}
            >
              <FaChevronLeft className="h-3 w-3" />
            </button>
            <span className="px-3" style={{ color: 'var(--color-text-secondary)' }}>
              Page {currentPage} of {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages}
              aria-label="Next page"
              className="rounded-md border p-2 disabled:opacity-40"
              style={{ borderColor: 'var(--color-border-light)', color: 'var(--color-text-primary)' }}
            >
              <FaChevronRight className="h-3 w-3" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
